import { useCallback, useState } from "react";
import FlowComponent from "@/components/flow/flow";
import { Button } from "@/components/ui/button";
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

import { createFileRoute } from "@tanstack/react-router";
import { Label } from "@/components/ui/label";
import { GitBranch, Plus } from "lucide-react";

export const Route = createFileRoute("/flow/")({
  component: RouteComponent,
});

const initialNodes = [
  {
    id: "1",
    data: { label: "Hello" },
    position: { x: 0, y: 0 },
    type: "resizeableNode",
  },
  {
    id: "2",
    data: { label: "World" },
    position: { x: 100, y: 100 },
    type: "resizeableNode",
  },
];

const initialEdges: any[] = [];

function RouteComponent() {
  const [nodes, setNodes] = useNodesState(initialNodes);
  const [edges, setEdges] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [dialogDetailOpen, setDialogDetailOpen] = useState(false);
  const [dialogAddOpen, setDialogAddOpen] = useState(false);

  const onConnect = useCallback(
    (params: any) => setEdges((eds) => addEdge(params, eds)),
    [],
  );
  const onNodesChange = useCallback(
    (changes: any) => setNodes((nds: any) => applyNodeChanges(changes, nds)),
    [],
  );
  const onEdgesChange = useCallback(
    (changes: any) => setEdges((eds: any) => applyEdgeChanges(changes, eds)),
    [],
  );

  const onNodeDoubleClick = (_: any, node: any) => {
    setSelectedNode(node);
    setDialogDetailOpen(true);
  };

  const onSaveDialog = () => {
    const findIndex = nodes.findIndex((n) => n.id === selectedNode.id);
    if (findIndex === -1) {
      const length = nodes.length;
      const newNode = {
        id: `${length + 1}`,
        position: {
          x: 100,
          y: 0,
        },
        data: { label: selectedNode.data.label },
        type: "resizeableNode",
      };
      setNodes((nds: any) => nds.concat(newNode));
    } else {
      const newNodes = [...nodes];
      newNodes[findIndex] = selectedNode;
      setNodes(newNodes);
    }

    setSelectedNode(null);
    setDialogDetailOpen(false);
    setDialogAddOpen(false);
  };

  return (
    <div className="aero:flex aero:flex-col aero:gap-4 aero:w-full aero:max-w-[1104px] aero:mx-auto">
      {/* Page Title */}
      <div className="aero:flex aero:items-center aero:gap-3">
        <div className="aero:w-10 aero:h-10 aero:rounded-lg aero:bg-brand-primary-50 aero:flex aero:items-center aero:justify-center">
          <GitBranch className="aero:text-brand-primary-500 aero:h-[27px] aero:w-[27px] aero:p-0.5" />
        </div>
        <h1 className="aero:text-xl aero:md:text-2xl aero:text-foreground aero:font-bold aero:leading-9">
          Flow Editor
        </h1>
      </div>

      {/* Main Card */}
      <Card className="aero:!rounded-lg aero:!border-neutral-300 aero:!p-4 aero:!gap-2 aero:!shadow-none">
        {/* Header with Add Button */}
        <div className="aero:flex aero:items-center aero:justify-between aero:pb-4 aero:border-b aero:border-neutral-400">
          <div className="aero:flex aero:items-center aero:gap-2">
            <span className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
            <h2 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
              Flow Diagram
            </h2>
          </div>
          <Button
            type="button"
            onClick={() => setDialogAddOpen(true)}
            className="aero:flex aero:items-center aero:gap-2"
          >
            <Plus className="aero:h-4 aero:w-4" />
            Add Node
          </Button>
        </div>

        {/* Flow Content */}
        <CardContent className="aero:!p-0 aero:!pt-4">
          <div style={{ height: "70vh" }}>
            <FlowComponent
              nodes={nodes}
              onNodesChange={onNodesChange}
              edges={edges}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeDoubleClick={onNodeDoubleClick}
            />
          </div>
        </CardContent>
      </Card>
      <Dialog open={dialogAddOpen} onOpenChange={setDialogAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add</DialogTitle>
            <DialogDescription>
              <div className="aero:grid aero:w-full aero:items-center aero:gap-1.5 aero:mb-4">
                <Label htmlFor="label">Label</Label>
                <Input
                  id="label"
                  value={selectedNode && selectedNode.data.label}
                  onChangeCapture={(event) => {
                    setSelectedNode({
                      ...selectedNode,
                      data: { label: event.currentTarget.value },
                    });
                  }}
                />
              </div>
            </DialogDescription>
            <DialogFooter>
              <Button onClick={onSaveDialog}>Save</Button>
            </DialogFooter>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogDetailOpen} onOpenChange={setDialogDetailOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detail</DialogTitle>
            <DialogDescription>
              <div className="aero:grid aero:w-full aero:items-center aero:gap-1.5 aero:mb-4">
                <Label htmlFor="label">Label</Label>
                <Input
                  id="label"
                  value={selectedNode && selectedNode.data.label}
                  onChangeCapture={(event) => {
                    setSelectedNode({
                      ...selectedNode,
                      data: { label: event.currentTarget.value },
                    });
                  }}
                />
              </div>
            </DialogDescription>
            <DialogFooter>
              <Button onClick={onSaveDialog}>Save</Button>
            </DialogFooter>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
